import { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useKickstarterForm } from '@/hooks/useKickstarterForm';

const KickstarterPage = () => {
  const { form, onSubmit } = useKickstarterForm();
  const { register, handleSubmit, formState: { errors, isSubmitting } } = form;
  const [formStatus, setFormStatus] = useState<{success?: boolean; message?: string}>({});

  const submitForm = async (data: { email: string }) => {
    try {
      console.log('Hero form submitting:', data);
      const result = await
       onSubmit(data);
      
      if (result.success) {
        setFormStatus({
          success: true,
          message: result.message || 'Thanks for signing up! We\'ll notify you when our Kickstarter launches.'
        });
        form.reset();
      } else {
        setFormStatus({
          success: false,
          message: result.error || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      console.error('Hero form error:', error);
      setFormStatus({
        success: false,
        message: 'An unexpected error occurred. Please try again later.'
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Join Our Kickstarter | Legendary Brushstrokes</title>
        <meta name="description" content="Be the first to know when our Kickstarter launches and get exclusive early-bird rewards!" />
      </Helmet>
      
      <div className="min-h-[calc(100vh-64px)] bg-slate-900 py-16 flex items-center">
        <div className="container">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div 
                className="flex flex-col"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="mb-6">
                  <div className="inline-block mb-4 px-3 py-1 bg-purple-900/30 border border-purple-800 rounded-full">
                    <span className="text-sm font-medium text-purple-300">Coming Soon</span>
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
                    Join the Legendary Brushstrokes <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 via-fuchsia-500 to-orange-500">Kickstarter</span>
                  </h1>
                  <p className="text-xl text-slate-300 mb-8">
                    Be among the first to experience our premier miniature painting kit featuring exclusive sculpts, quality paints, and premium brushes.
                  </p>
                
                  <div className="bg-slate-800/70 p-6 rounded-lg border border-slate-700 mb-8">
                  {formStatus.success ? (
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-4"
                    >
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-900/50 text-green-400 mb-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p className="text-green-400 font-medium">{formStatus.message}</p>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit(submitForm)}>
                      <p className="text-lg font-medium text-white mb-3">Get Kickstarter Alerts & Early Access!</p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <input
                          type="email"
                          placeholder="Your email address"
                          className={`flex-grow px-4 py-3 bg-slate-700 border ${errors.email ? 'border-red-500' : 'border-slate-600'} rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500`}
                          {...register('email')}
                        />
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="btn-primary whitespace-nowrap"
                        >
                          {isSubmitting ? 'Submitting...' : 'Notify Me On Launch!'}
                        </button>
                      </div>
                      {errors.email && (
                        <p className="mt-2 text-red-400 text-sm">{errors.email.message as string}</p>
                      )}
                      {formStatus.message && !formStatus.success && (
                        <p className="mt-2 text-red-400 text-sm">{formStatus.message}</p>
                      )}
                      {import.meta.env.DEV && (
                        <div className="mt-2 p-1 border border-yellow-700 bg-yellow-900/30 rounded-md">
                          <p className="text-yellow-400 text-xs">Debug: Form will submit to API endpoint</p>
                        </div>
                      )}
                    </form>
                  )}
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-slate-800 rounded-lg p-8 shadow-xl shadow-purple-900/10"
              >
                <h2 className="text-2xl font-bold text-white mb-6">Why Support Our Kickstarter?</h2>
                
                <ul className="space-y-6">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 bg-purple-900/30 p-2 rounded-lg mr-4">
                      <svg className="h-6 w-6 text-purple-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Exclusive Early Bird Rewards</h3>
                      <p className="text-slate-300">Special pricing and exclusive items only available to our Kickstarter backers.</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 bg-purple-900/30 p-2 rounded-lg mr-4">
                      <svg className="h-6 w-6 text-purple-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Limited Edition Sets</h3>
                      <p className="text-slate-300">Special themed sets only available during the Kickstarter campaign.</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 bg-purple-900/30 p-2 rounded-lg mr-4">
                      <svg className="h-6 w-6 text-purple-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.933 12.8a1 1 0 000-1.6L6.6 7.2A1 1 0 005 8v8a1 1 0 001.6.8l5.333-4zM19.933 12.8a1 1 0 000-1.6l-5.333-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.333-4z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">First Access</h3>
                      <p className="text-slate-300">Be the first to receive Legendary Brushstrokes before it's available to the general public.</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 bg-purple-900/30 p-2 rounded-lg mr-4">
                      <svg className="h-6 w-6 text-purple-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Backer Input</h3>
                      <p className="text-slate-300">Help shape future Legendary Brushstrokes sets with your feedback and suggestions.</p>
                    </div>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default KickstarterPage;
